/**
 * Sidebar Navigation Component
 * - Desktop (>1024px): Collapsible left sidebar
 * - Tablet (769–1024px): Top bar + slide-in sidebar overlay
 * - Mobile (<=768px): Thin top bar + bottom tab bar + bottom sheet
 */

import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import {
  FaHome,
  FaCalculator,
  FaDatabase,
  FaTags,
  FaUsers,
  FaGlobe,
  FaMoneyBillWave,
  FaChartLine,
  FaFileAlt,
  FaListAlt,
  FaExclamationTriangle,
  FaBoxes,
  FaChartBar,
  FaUserCircle,
  FaSignOutAlt,
  FaChevronDown,
  FaChevronRight,
  FaTimes,
  FaBuilding,
  FaBriefcase,
  FaTrophy,
  FaDoorOpen,
  FaRobot,
  FaEdit,
  FaBars,
  FaWeight,
  FaBullhorn,
  FaWarehouse,
  FaShoppingCart,
  FaBullseye,
  FaRocket,
  FaProjectDiagram,
  FaReceipt,
  FaFileInvoice,
  FaFileInvoiceDollar,
  FaFileExcel,
  FaLayerGroup,
  FaUserTie,
  FaChartPie,
  FaStream,
  FaShip,
  FaCloud,
  FaEnvelope,
  FaKey,
  FaBalanceScale,
  FaUserCog,
  FaCalendarAlt,
  FaShippingFast,
  FaShopify
} from 'react-icons/fa';
import ThemeToggle from './ThemeToggle';
import { cn } from '../lib/utils';
import { confirmLeave } from '../hooks/useUnsavedChangesGuard';
import { NOVELTY_PIPELINE_ROLES, SALES_DATA_ROLES } from '../constants/activationRoles';
import { noveltyPipelineEnabled, shipmentTimelineEnabled } from '../constants/featureFlags';

const Sidebar = () => {
  const navigate = useNavigate();
  const {
    user, logout, hasRole, isAdmin, isManager, hasMarketingAccess, isBrandManager,
    hasActivationAccess, isActivationOnly,
  } = useAuth();
  const location = useLocation();
  const { isCollapsed, isSidebarOpen, setIsSidebarOpen, isMobile, toggleSidebar } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const isDesktop = !isMobile; // Tablet uses same sidebar as desktop

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const handleToggleSidebar = () => {
    toggleSidebar();
    if (isDesktop && !isCollapsed) {
      setExpandedMenus({});
    }
  };

  const handleLogout = () => {
    // Asked before logout(): once the cleared auth renders, ProtectedRoute unmounts the page
    // and its unsaved edits are gone whatever the user answers.
    if (!confirmLeave()) return;
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
  };

  // Brand managers only see Marketing menu with limited items.
  // Activation team roles (VM, Local Brand, Social Media, KOL, E-commerce) see
  // only Marketing > Activation. Both collapse the sidebar the same way.
  const brandManagerOnly = isBrandManager();
  const activationOnly = isActivationOnly();
  const reducedNav = brandManagerOnly || activationOnly;
  // The sales-data entries outside Marketing (Product Sales, Sales YoY Report, Projections,
  // Stock & Inventory, Existing Brand BP, Purchase & BP Dashboard, Sales Reporting, Beauty
  // Advisor Sales, Sales Data Consistency Check, Supplier Report Templates): SALES_DATA_ROLES.
  // brand_manager has Marketing > Sales; plain user and the activation team are refused.
  const salesDataReader = hasRole(...SALES_DATA_ROLES);
  const noveltyReader = hasRole(...NOVELTY_PIPELINE_ROLES);

  const handleMouseEnter = (menuName) => {
    if (isCollapsed && isDesktop) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => setHoveredMenu(menuName), 200);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredMenu(null), 200);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const menuItems = [
    // Overview
    ...(!reducedNav ? [{ title: 'Dashboard', icon: <FaHome />, path: '/', single: true }] : []),
    // Core operations: pricing
    ...(!reducedNav ? [{
      title: 'Price Management',
      icon: <FaChartLine />,
      submenu: [
        { title: 'Price Calculator', path: '/calculator', icon: <FaCalculator /> },
        { title: 'Price Structures', path: '/price-structures', icon: <FaChartLine /> },
        { title: 'Brand List MRP', path: '/brand-list-mrp', icon: <FaListAlt /> },
        { title: 'Unconfirmed MRPs', path: '/brand-list-mrp/unconfirmed', icon: <FaExclamationTriangle /> },
        { title: 'Templates', path: '/templates', icon: <FaFileAlt /> },
        // /budgets admits admin, manager and brand_manager; brand_manager reaches it from Marketing.
        { title: 'Brand Budgets', path: '/budgets', icon: <FaMoneyBillWave />, show: isManager() }
      ]
    }] : []),
    // Core operations: stock & supply chain
    ...(!reducedNav ? [{
      title: 'Stock Management',
      icon: <FaWarehouse />,
      submenu: [
        { title: 'Product Catalog', path: '/products', icon: <FaListAlt /> },
        { title: 'Stock & Inventory', path: '/inventory', icon: <FaBoxes />, show: salesDataReader },
        { title: 'Purchase Orders', path: '/purchase-orders', icon: <FaShoppingCart /> },
        { title: 'Inventory Timeline', path: '/inventory-timeline', icon: <FaStream /> },
        // Shipment Timeline: off unless REACT_APP_SHIPMENT_TIMELINE_ENABLED=true.
        { title: 'Shipment Timeline', path: '/shipment-timeline', icon: <FaShip />, show: shipmentTimelineEnabled() },
        // Arrival Pipeline (All Products) and InvenTree Parts List: admin only while they
        // are tested. Novelty Pipeline is under Marketing.
        // Shopify Stock writes to the live storefront's stock: admin only.
        ...(isAdmin() ? [
          { title: 'Arrival Pipeline', path: '/arrival-pipeline', icon: <FaShippingFast /> },
          { title: 'Shopify Stock', path: '/shopify-stock', icon: <FaShopify /> },
          { title: 'InvenTree Parts List', path: '/inventree-parts', icon: <FaListAlt /> }
        ] : [])
      ]
    }] : []),
    // Sales & budget forecasting
    ...(!reducedNav ? [{
      title: 'Budget Projections',
      icon: <FaChartLine />,
      submenu: [
        { title: 'Projections', path: '/projections-v2', icon: <FaChartLine />, show: salesDataReader },
        { title: 'Sales', path: '/budget-projections/product-sales', icon: <FaBoxes />, show: salesDataReader },
        { title: 'Sales YoY Report', path: '/budget-projections/yoy-report', icon: <FaChartLine />, show: salesDataReader },
        // Brand Ranking is admin-only.
        ...(isAdmin() ? [{ title: 'Brand Ranking', path: '/brand-ranking', icon: <FaTrophy /> }] : []),
        { title: 'Brand Targets', path: '/brand-targets', icon: <FaBullseye /> }
      ]
    }] : []),
    // Marketing
    // Activation is the only entry an activation-team role sees; the rest of the
    // group carries `show` so those roles get a one-item Marketing menu.
    ...(hasMarketingAccess() ? [{
      title: 'Marketing',
      icon: <FaBullhorn />,
      submenu: [
        { title: 'Activation', path: '/marketing/activations', icon: <FaRocket />, show: hasActivationAccess() },
        { title: 'Marketing Budget', path: '/budgets', icon: <FaMoneyBillWave />, show: !activationOnly },
        { title: 'Sales', path: '/budget-projections/product-sales', icon: <FaChartLine />, show: !activationOnly },
        { title: 'Outpost Sales', path: '/outpost-sales', icon: <FaFileAlt />, show: !activationOnly },
        { title: 'New Product Launch List', path: '/marketing/new-product-launch-list', icon: <FaRocket />, show: !activationOnly },
        // Novelty Pipeline: off unless REACT_APP_NOVELTY_PIPELINE_ENABLED=true.
        { title: 'Novelty Pipeline', path: '/arrival-pipeline/novelties', icon: <FaShippingFast />, show: noveltyReader && noveltyPipelineEnabled() },
        { title: 'Expenses', path: '/marketing/expenses', icon: <FaReceipt />, show: !activationOnly },
        { title: 'CAPEX', path: '/capex', icon: <FaWarehouse />, show: !activationOnly },
        { title: 'Reports', path: '/marketing/reports', icon: <FaChartBar />, show: !activationOnly },
        { title: 'Analytics', path: '/marketing/analytics', icon: <FaChartPie />, show: !activationOnly },
        ...(isAdmin() ? [
          { title: 'Quotes', path: '/marketing/quotes', icon: <FaFileInvoice /> },
          { title: 'Invoices', path: '/marketing/invoices', icon: <FaFileInvoiceDollar /> }
        ] : [])
      // This submenu had no `show` filter; without it every hidden entry renders.
      ].filter(item => item.show !== false)
    }] : []),
    // Strategic planning
    ...(!reducedNav ? [{
      title: 'Business Plan',
      icon: <FaBriefcase />,
      submenu: [
        { title: 'Saved Plans', path: '/business-plans', icon: <FaListAlt /> },
        { title: 'Plan Editor', path: '/business-plan', icon: <FaEdit /> },
        { title: 'Existing Brand BP', path: '/existing-brand-business-plans', icon: <FaBriefcase />, show: salesDataReader },
        { title: 'SOB Matrix', path: '/sob-matrix', icon: <FaChartPie /> },
        { title: 'Product Line Extrapolator', path: '/business-plan/product-line-extrapolations', icon: <FaProjectDiagram /> },
        { title: 'AI Assistant', path: '/business-plan/ai-assistant', icon: <FaRobot /> },
        { title: 'Email Templates', path: '/email-templates', icon: <FaEnvelope />, show: isManager() }
      ]
    }] : []),
    // Purchase & BP Dashboard
    ...(salesDataReader ? [{ title: 'Purchase & BP Dashboard', icon: <FaShoppingCart />, path: '/purchase-bp-dashboard', single: true }] : []),
    // Sales Reporting
    ...(salesDataReader ? [{ title: 'Sales Reporting', icon: <FaChartBar />, path: '/sales-reporting', single: true }] : []),
    // GA Management — the two pages differ in who may open them, so the group is shown
    // to managers for GA PO Selection and GA Review carries its own admin gate. Listing
    // GA Review for a manager would put a link in the menu that /ga-data refuses.
    ...(isManager() ? [{
      title: 'GA Management',
      icon: <FaCloud />,
      submenu: [
        // Generate, pull and send all write to the GA system.
        { title: 'GA Review', path: '/ga-data', icon: <FaCloud />, show: isAdmin() },
        // NetSuite ↔ InvenTree matching + GA upload files.
        { title: 'GA PO Selection', path: '/ga-new-po', icon: <FaFileInvoiceDollar /> }
      ]
    }] : []),
    ...(isAdmin() ? [{ title: 'API Keys', icon: <FaKey />, path: '/api-keys', single: true }] : []),
    // Reference data & admin (bottom)
    ...(!reducedNav ? [{
      title: 'Master Data',
      icon: <FaDatabase />,
      // Sorted A→Z by title. Role-gated entries (marketing items, Users) carry a
      // `show` predicate so they keep their visibility rules while still landing
      // in alphabetical position; the filter drops the ones that don't apply.
      submenu: [
        { title: 'BA Setup', path: '/ba-setup', icon: <FaUserCog />, show: isAdmin() },
        { title: 'Beauty Advisor Sales', path: '/beauty-advisor-sales', icon: <FaUsers />, show: salesDataReader },
        { title: 'Beauty Advisor Sales Export', path: '/advisor-sales-export', icon: <FaFileExcel />, show: isAdmin() },
        { title: 'Brands', path: '/brands', icon: <FaTags /> },
        { title: 'Contractors', path: '/marketing/contractors', icon: <FaUserTie />, show: hasMarketingAccess() },
        { title: 'Currencies', path: '/currencies', icon: <FaMoneyBillWave /> },
        { title: 'Door Channels', path: '/door-channels', icon: <FaStream /> },
        { title: 'Door Sales', path: '/door-sales', icon: <FaFileAlt />, show: isAdmin() },
        { title: 'Door Segments', path: '/segments', icon: <FaLayerGroup /> },
        { title: 'Door Weights', path: '/brand-door-weights', icon: <FaWeight /> },
        { title: 'Doors', path: '/doors', icon: <FaDoorOpen /> },
        { title: 'Markets', path: '/markets', icon: <FaGlobe /> },
        { title: 'Marketing Categories', path: '/marketing/categories', icon: <FaLayerGroup />, show: hasMarketingAccess() },
        { title: 'Marketing Items', path: '/marketing/items', icon: <FaListAlt />, show: hasMarketingAccess() },
        { title: 'Marketing Processes', path: '/marketing/processes', icon: <FaProjectDiagram />, show: isManager() },
        { title: 'New Product Launch List', path: '/new-product-launch-list', icon: <FaRocket /> },
        { title: 'Outpost Sales', path: '/outpost-sales', icon: <FaFileAlt />, show: isManager() },
        { title: 'Pricing Channels', path: '/pricing-channels', icon: <FaTags /> },
        { title: 'Product Sales', path: '/budget-projections/product-sales', icon: <FaBoxes />, show: salesDataReader },
        { title: 'Purchase Orders', path: '/master-data/purchase-orders', icon: <FaFileInvoiceDollar /> },
        { title: 'Reporting Groups', path: '/brand-groups', icon: <FaLayerGroup /> },
        { title: 'Sales Data Consistency Check', path: '/data-consistency', icon: <FaBalanceScale />, show: salesDataReader },
        { title: 'Seasonality Calendar', path: '/master-data/seasonality-calendar', icon: <FaCalendarAlt />, show: isAdmin() },
        { title: 'Supplier Report Templates', path: '/supplier-report-templates', icon: <FaFileInvoice />, show: salesDataReader },
        { title: 'Suppliers', path: '/suppliers', icon: <FaBuilding /> },
        { title: 'Users', path: '/users', icon: <FaUsers />, show: isManager() }
      ].filter(item => item.show !== false)
    }] : [])
  ];

  // Keep the group holding the current page open. Each route builds its own
  // MainLayout, so the sidebar remounts on every navigation and expandedMenus
  // starts empty again — without this the group a user just clicked through
  // closes under them. It only ever opens a group: a group the user closed by
  // hand stays closed until the path moves into it.
  const pathname = location.pathname;
  const ownerTitles = menuItems
    .filter((item) => item.submenu?.some((s) => s.path && (pathname === s.path || pathname.startsWith(`${s.path}/`))))
    .map((item) => item.title)
    .join('\u0000');
  useEffect(() => {
    const titles = ownerTitles ? ownerTitles.split('\u0000') : [];
    if (titles.length === 0) return;
    setExpandedMenus((prev) => {
      if (titles.every((t) => prev[t])) return prev; // already open: no re-render
      const next = { ...prev };
      titles.forEach((t) => { next[t] = true; });
      return next;
    });
  }, [ownerTitles]);

  // Bottom tab bar items — fixed set for quick access
  const tabBarItems = reducedNav
    ? (menuItems.find(item => item.submenu)?.submenu.slice(0, 4).map(sub => ({ ...sub, single: true })) || [])
    : [
        { title: 'Home', icon: <FaHome />, path: '/', single: true },
        { title: 'Price Structures', icon: <FaTags />, path: '/price-structures', single: true },
        { title: 'Saved Plans', icon: <FaListAlt />, path: '/business-plans', single: true },
        ...(isManager() ? [{ title: 'Budgets', icon: <FaMoneyBillWave />, path: '/budgets', single: true }] : []),
      ];
  // Short labels for the bottom bar so 4 items + More fit on narrow phones.
  const tabBarShortLabels = { 'Price Structures': 'Prices', 'Saved Plans': 'Plans' };
  const tabLabels = {};

  // ─── Shared nav item renderer ────────────────────────────────
  const renderNavItem = (item, index, { collapsible = false } = {}) => (
    <div key={index} className="mb-0.5">
      {item.single ? (
        <NavLink
          to={item.path}
          end={item.path === '/'}
          onClick={handleNavClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
              isActive
                ? 'bg-primary-600 text-white [&>span]:text-white'
                : 'text-lm-text-muted dark:text-gray-200 hover:bg-lm-surface-2 dark:hover:bg-dm-surface-2 hover:text-lm-text-primary dark:hover:text-white',
              collapsible && isCollapsed && 'justify-center px-2'
            )
          }
          title={collapsible && isCollapsed ? item.title : undefined}
        >
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            {item.icon}
          </span>
          {(!collapsible || !isCollapsed) && (
            <span className="text-sm font-medium truncate">{item.title}</span>
          )}
        </NavLink>
      ) : (
        <>
          <div
            className="relative"
            onMouseEnter={() => collapsible && handleMouseEnter(item.title)}
            onMouseLeave={() => collapsible && handleMouseLeave()}
          >
            <button
              onClick={() => (!collapsible || !isCollapsed) && toggleMenu(item.title)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                'text-lm-text-muted dark:text-gray-200 hover:bg-lm-surface-2 dark:hover:bg-dm-surface-2 hover:text-lm-text-primary dark:hover:text-white',
                expandedMenus[item.title] && 'bg-lm-surface-2 dark:bg-dm-surface-2',
                collapsible && isCollapsed && 'justify-center px-2'
              )}
              title={collapsible && isCollapsed ? item.title : undefined}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                {item.icon}
              </span>
              {(!collapsible || !isCollapsed) && (
                <>
                  <span className="text-sm font-medium flex-1 text-left truncate">
                    {item.title}
                  </span>
                  <span className="text-lm-text-muted dark:text-gray-400">
                    {expandedMenus[item.title] ? (
                      <FaChevronDown className="w-3 h-3" />
                    ) : (
                      <FaChevronRight className="w-3 h-3" />
                    )}
                  </span>
                </>
              )}
            </button>

            {/* Popup menu for collapsed desktop sidebar */}
            {collapsible && isCollapsed && hoveredMenu === item.title && (
              <div
                className="fixed left-16 bg-white dark:bg-dm-surface-1 border border-lm-border dark:border-dm-border rounded-lg shadow-lg py-2 min-w-[200px] z-50"
                style={{ top: `${index * 48 + 120}px` }}
                onMouseEnter={() => handleMouseEnter(item.title)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="px-3 py-2 text-xs font-semibold text-lm-text-muted dark:text-dm-text-muted uppercase tracking-wider border-b border-lm-border dark:border-dm-border mb-2">
                  {item.title}
                </div>
                {item.submenu.filter(subItem => subItem.show !== false).map((subItem, subIndex) => (
                  <NavLink
                    key={subIndex}
                    to={subItem.path}
                    end
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 px-3 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-primary-600 text-white [&>span]:text-white'
                          : 'text-lm-text-muted dark:text-gray-300 hover:bg-lm-surface-2 dark:hover:bg-dm-surface-2 hover:text-lm-text-primary dark:hover:text-white'
                      )
                    }
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-xs">
                      {subItem.icon}
                    </span>
                    <span className="truncate">{subItem.title}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {expandedMenus[item.title] && (!collapsible || !isCollapsed) && (
            <div className="mt-1 ml-4 pl-4 border-l border-lm-border dark:border-dm-border space-y-0.5">
              {item.submenu.filter(subItem => subItem.show !== false).map((subItem, subIndex) => (
                <NavLink
                  key={subIndex}
                  to={subItem.path}
                  end
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
                      isActive
                        ? 'bg-primary-600 text-white [&>span]:text-white'
                        : 'text-lm-text-muted dark:text-gray-300 hover:bg-lm-surface-2 dark:hover:bg-dm-surface-2 hover:text-lm-text-primary dark:hover:text-white'
                    )
                  }
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-xs">
                    {subItem.icon}
                  </span>
                  <span className="truncate">{subItem.title}</span>
                </NavLink>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  // ─── Shared: User info block ──────────────────────────────────
  const renderUserInfo = (compact = false) => (
    <div className={cn('flex items-center gap-2.5', !compact && 'p-2 rounded-xl bg-lm-surface-2/50 dark:bg-dm-surface-2/50')}>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
        <FaUserCircle className="w-4 h-4 text-white" />
      </div>
      <div className="min-w-0">
        <span className="block font-semibold text-sm truncate text-lm-text-primary dark:text-dm-text-primary leading-none">
          {user?.firstName || user?.username || 'User'}
        </span>
        <span className="block text-xs text-lm-text-muted dark:text-dm-text-muted capitalize truncate leading-none mt-1">
          {user?.role || 'User'}
        </span>
      </div>
    </div>
  );

  // ─── Shared: Logout button ────────────────────────────────────
  const renderLogout = (compact = false) => (
    <button
      onClick={handleLogout}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
        'text-lm-text-muted hover:bg-error/10 hover:text-error',
        'dark:text-dm-text-muted dark:hover:bg-error/20 dark:hover:text-error',
        compact && 'justify-center px-2'
      )}
      title={compact ? 'Logout' : undefined}
    >
      <FaSignOutAlt className="w-5 h-5 flex-shrink-0" />
      {!compact && <span className="text-sm font-medium">Logout</span>}
    </button>
  );

  // ═══════════════════════════════════════════════════════════════
  // MOBILE: Thin top bar + bottom tab bar + bottom sheet
  // ═══════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
        {/* Top bar — hamburger opens the side drawer (replaces the old bottom
            "More" tab). Fixed so it's reachable from any scroll position. */}
        <header className="fixed top-0 left-0 right-0 z-40 h-12 flex items-center gap-2 px-3 bg-lm-bg-sidebar dark:bg-dm-bg-sidebar border-b border-lm-border dark:border-dm-border">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 -ml-1 rounded-lg text-lm-text-primary dark:text-dm-text-primary hover:bg-lm-surface-2 dark:hover:bg-dm-surface-2"
            aria-label="Open menu"
          >
            <FaBars className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/cropped-cropped-bestinbrands-logo.webp" alt="" className="h-6 w-6 object-contain" />
            <span className="font-display font-bold text-base tracking-tight leading-none text-lm-text-primary dark:text-dm-text-primary">
              BIB Intelligence
            </span>
          </div>
        </header>

        {/* Bottom tab bar — fixed destinations only (More moved to the top
            hamburger). */}
        <nav className="fixed bottom-0 left-0 right-0 z-40 h-14 flex items-stretch bg-lm-bg-sidebar dark:bg-dm-bg-sidebar border-t border-lm-border dark:border-dm-border">
          {tabBarItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-lm-text-muted dark:text-dm-text-muted'
                )
              }
            >
              <span className="text-base">{item.icon}</span>
              <span className="text-[10px] font-medium leading-none">
                {tabBarShortLabels[item.title] || tabLabels[item.title] || item.title}
              </span>
            </NavLink>
          ))}
        </nav>

        {/* Side drawer backdrop */}
        <div
          className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-300',
            isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          )}
          onClick={() => setIsSidebarOpen(false)}
        />

        {/* Side drawer — slides in from the left, full height, leaving the
            bottom tab bar reachable. Replaces the old bottom sheet. */}
        <div
          className={cn(
            'fixed top-0 left-0 z-50 h-full w-[82%] max-w-xs flex flex-col bg-lm-bg-sidebar dark:bg-dm-bg-sidebar shadow-2xl transition-transform duration-300 ease-smooth',
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {/* Header — brand + close */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-lm-border dark:border-dm-border">
            <div className="flex items-center gap-2.5">
              <img src="/cropped-cropped-bestinbrands-logo.webp" alt="" className="h-7 w-7 object-contain" />
              <span className="font-display font-bold text-lg tracking-tight leading-none text-lm-text-primary dark:text-dm-text-primary">
                BIB Intelligence
              </span>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 -mr-2 rounded-lg text-lm-text-muted hover:bg-lm-surface-2 dark:text-dm-text-muted dark:hover:bg-dm-surface-2"
              aria-label="Close menu"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-2 border-b border-lm-border dark:border-dm-border">
            {renderUserInfo(true)}
          </div>

          {/* Full nav */}
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {menuItems.map((item, index) => renderNavItem(item, index))}
          </div>

          {/* Footer */}
          <div className="px-3 py-3 border-t border-lm-border dark:border-dm-border space-y-2">
            <div className="flex justify-start px-3">
              <ThemeToggle variant="default" />
            </div>
            {renderLogout()}
          </div>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // DESKTOP: Collapsible left sidebar
  // ═══════════════════════════════════════════════════════════════
  return (
    <aside
      className={cn(
        'sidebar fixed left-0 top-0 h-screen z-50 flex flex-col transition-all duration-300 ease-smooth',
        'bg-lm-bg-sidebar dark:bg-dm-bg-sidebar border-r border-lm-border dark:border-dm-border',
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header */}
      <div className={cn(
        'flex items-center px-3 py-4 border-b border-lm-border dark:border-dm-border',
        isCollapsed ? 'justify-center' : 'justify-between'
      )}>
        {!isCollapsed && (
          <div className="flex items-center gap-2.5">
            <img src="/cropped-cropped-bestinbrands-logo.webp" alt="" className="h-7 w-7 object-contain" />
            <span className="font-display font-bold text-lg tracking-tight leading-none text-lm-text-primary dark:text-dm-text-primary">
              BIB Intelligence
            </span>
          </div>
        )}
        <button
          onClick={handleToggleSidebar}
          className={cn(
            'p-2 rounded-lg text-lm-text-muted transition-colors',
            'hover:bg-lm-surface-2 dark:text-dm-text-muted dark:hover:bg-dm-surface-2'
          )}
          aria-label="Toggle sidebar"
        >
          <FaBars className="w-5 h-5" />
        </button>
      </div>

      {/* User info */}
      <div className={cn(
        'px-3 py-2.5 border-b border-lm-border dark:border-dm-border',
        isCollapsed && 'flex justify-center px-2'
      )}>
        <div className={cn(
          'flex items-center gap-2.5 p-2 rounded-xl bg-lm-surface-2/50 dark:bg-dm-surface-2/50',
          isCollapsed && 'p-2'
        )}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
            <FaUserCircle className="w-4 h-4 text-white" />
          </div>
          {!isCollapsed && (
            <div className="min-w-0 h-8 flex flex-col justify-center">
              <span className="block font-semibold text-sm truncate text-lm-text-primary dark:text-dm-text-primary leading-none">
                {user?.firstName || user?.username || 'User'}
              </span>
              <span className="block text-xs text-lm-text-muted dark:text-dm-text-muted capitalize truncate leading-none mt-1">
                {user?.role || 'User'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 custom-scrollbar">
        {menuItems.map((item, index) => renderNavItem(item, index, { collapsible: true }))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-lm-border dark:border-dm-border space-y-2">
        <div className={cn('flex', isCollapsed ? 'justify-center' : 'justify-start')}>
          <ThemeToggle variant={isCollapsed ? 'compact' : 'default'} />
        </div>
        {renderLogout(isCollapsed)}
      </div>
    </aside>
  );
};

export default Sidebar;
